import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNotes, postNotes, getEsimNotes } from "../../services/api";
import * as PR from "../../prime-modules/index";
import moment from "moment";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./Notes.scss";

const Notes = ({ orderId, iccid, refresh, title = "Notes", canAdd, emptyMessage }) => {
  const dispatch = useDispatch();
  const toast = React.useRef(null);
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData?.sessionId };
  }, [adminData?.sessionId]);
  const [notes, setNotes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const targetId = iccid || orderId;
  const isAddAllowed = canAdd !== undefined ? canAdd : Boolean(orderId);
  const defaultEmptyMsg = emptyMessage || (iccid ? "No order notes available" : "No notes available");

  const displayNotes = useCallback(() => {
    if (!targetId) return;
    setLoading(true);

    const handleResponse = (response) => {
      setLoading(false);
      if (response.result === "SUCCESS") {
        const rawNotes = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.notes)
          ? response.data.notes
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        const sortedNotes = rawNotes.sort(
          (a, b) =>
            new Date(b.createdTs || b.createdAt || b.createdDate || 0) -
            new Date(a.createdTs || a.createdAt || a.createdDate || 0)
        );
        setNotes(sortedNotes);
      } else {
        setNotes([]);
      }
    };

    if (iccid) {
      getEsimNotes(iccid, headers, dispatch, handleResponse);
    } else if (orderId) {
      getNotes(dispatch, headers, orderId, handleResponse);
    }
  }, [dispatch, headers, targetId, iccid, orderId]);

  useEffect(() => {
    displayNotes();
  }, [displayNotes, refresh]);

  const handleSave = (values, { resetForm }) => {
    const { description } = values;
    if (description.trim() && orderId) {
      const obj = { description };
      const saveNotesResponse = (response) => {
        if (response.result === "SUCCESS") {
          const newNote = {
            userName: adminData?.username,
            description,
            createdTs: new Date(),
          };
          setNotes((prevNotes) => [newNote, ...prevNotes]);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Saved successfully",
          });
          resetForm();
          setIsEditing(false);
        } else if (response.result === "FAILED" && response.error) {
          const error = response.error;
          toast.current?.show({
            severity: error.severity || "error",
            summary: "Error",
            detail: error.errorMsg ? error.errorMsg : error.summary,
          });
        }
      };
      postNotes(obj, dispatch, headers, orderId, saveNotesResponse);
    }
  };

  const handleCancel = (resetForm) => {
    resetForm();
    setIsEditing(false);
  };

  const headerTemplate = () => (
    <div className="notes-header">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-book" style={{ fontSize: "14px", color: "#fff" }}></i>
        <span>{title}</span>
      </div>
      <div className="flex align-items-center gap-2">
        <PR.Button
          icon={loading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
          className="refresh-notes-btn"
          onClick={displayNotes}
          disabled={loading}
          tooltip="Refresh Notes"
          tooltipOptions={{ position: "bottom" }}
        />
        {isAddAllowed && (
          <PR.Button
            icon="pi pi-plus"
            className="p-button-sm p-button-secondary ml-10"
            onClick={() => setIsEditing(true)}
            tooltip="Add Note"
            tooltipOptions={{ position: "bottom" }}
          />
        )}
      </div>
    </div>
  );

  const timeFormat = (createdTs) => {
    if (!createdTs) return "-";
    const momentTs = moment(createdTs);
    return momentTs.isValid() ? momentTs.format("ddd, MMM DD, YYYY | hh:mm A") : "-";
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .required("Description is required")
      .min(3, "Description must be at least 3 characters")
      .max(1000, "Description must be max 1000 characters"),
  });

  return (
    <PR.Panel header={headerTemplate} className="notes-panel">
      <div className="pt-2 pb-2">
        {isEditing && (
          <Formik
            initialValues={{ title: "", description: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSave}
          >
            {({ resetForm }) => (
              <Form className="p-mb-2 add-notes mb-3">
                <div>
                  <Field
                    name="description"
                    as={PR.InputTextarea}
                    placeholder="Write your note"
                    className="p-mr-2"
                    autoResize
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      maxHeight: "300px",
                    }}
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="p-error text-sm"
                  />
                </div>
                <div className="p-mt-2 text-right mb-2">
                  <PR.Button
                    type="submit"
                    label="Save"
                    className="p-button-secondary save"
                  />
                  <PR.Button
                    label="Cancel"
                    onClick={() => handleCancel(resetForm)}
                    className="p-button-danger"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}

        {loading && notes.length === 0 ? (
          <div className="flex align-items-center justify-content-center py-5">
            <PR.ProgressSpinner style={{ width: "35px", height: "35px" }} strokeWidth="4" />
          </div>
        ) : notes.length > 0 ? (
          <div className="notes-block">
            {notes.map((note, index) => (
              <div key={index} className="notes-list">
                <div className="description">
                  <span
                    className="p-mr-2"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      width: "100%",
                    }}
                  >
                    {note.description || note.note || note.comment || note.message || "-"}
                  </span>
                </div>
                <div className="time flex align-items-center justify-content-between">
                  <span className="mr-12">Posted by: {note.userName || note.username || note.user || note.createdBy || "Admin"}</span>
                  <span className="mr-12">{timeFormat(note.createdTs || note.createdAt || note.createdDate)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-notes-found">
            <i className="pi pi-info-circle"></i>
            <p>{defaultEmptyMsg}</p>
          </div>
        )}

        <PR.Toast ref={toast} position="top-right" />
      </div>
    </PR.Panel>
  );
};

export default Notes;
